/**
 * Раздельные статусные модели (ключевой принцип ТЗ п.1).
 * Значения должны совпадать с enum'ами в prisma/schema.prisma.
 *
 * Выпуска и наряда у спецтехники нет: задание выдаётся сразу на смену, без промежуточной
 * разнарядки на парк, и своих статусов у них не осталось.
 */

export const SHIFT_STATUSES = [
  'PLANNED',
  'CONFIRMED',
  'REJECTED',
  'READY',
  'BLOCKED',
  'ON_LINE',
  'CLOSED',
] as const;
export type ShiftStatus = (typeof SHIFT_STATUSES)[number];

/**
 * Состояния путевого листа. Ветвятся по режиму работы единицы: ежедневная делает один
 * выезд за смену, дежурная открывает дежурство и выезжает по вызовам сколько потребуется.
 * Начало и конец у обеих общие.
 */
export const WAYBILL_STATUSES = [
  'ISSUED',
  // ── ежедневная эксплуатация ──
  'ON_WAY',
  'ON_SITE',
  'RETURNED',
  // ── дежурство ОДС ──
  'DUTY_OPEN',
  'ON_CALL',
  'ON_DUTY',
  'DUTY_CLOSED',
  // ── общий конец ──
  'CLOSED',
  'CANCELLED',
] as const;
export type WaybillStatus = (typeof WAYBILL_STATUSES)[number];

export const INTEGRATION_STATES = [
  'PENDING',
  'AWAITING_SIGNATURE',
  'SENT',
  'SUPERSEDED',
  'ERROR',
] as const;
export type IntegrationState = (typeof INTEGRATION_STATES)[number];

/** Человекочитаемые подписи для интерфейсов. */
export const SHIFT_STATUS_LABELS: Record<ShiftStatus, string> = {
  PLANNED: 'Запланирована',
  CONFIRMED: 'Подтверждена водителем',
  REJECTED: 'Отказ водителя',
  READY: 'Готов к выпуску',
  BLOCKED: 'Заблокирована',
  ON_LINE: 'На линии',
  CLOSED: 'Закрыта',
};

export const WAYBILL_STATUS_LABELS: Record<WaybillStatus, string> = {
  ISSUED: 'Выдан',
  ON_WAY: 'В пути на объект',
  ON_SITE: 'Работы на объекте',
  RETURNED: 'Вернулся в парк',
  DUTY_OPEN: 'Дежурство открыто',
  ON_CALL: 'Выезд по вызову',
  ON_DUTY: 'Работы по вызову',
  DUTY_CLOSED: 'Дежурство закрыто',
  CLOSED: 'Закрыт',
  CANCELLED: 'Аннулирован',
};

export const INTEGRATION_STATE_LABELS: Record<IntegrationState, string> = {
  PENDING: 'В очереди',
  // Не ошибка и не очередь: конверт получен, ждём, пока работник подпишет своим ключом.
  AWAITING_SIGNATURE: 'Ждёт подписи',
  SENT: 'Отправлено',
  /**
   * Работник переписал осмотр, пока прежняя запись ждала подписи. Это нормальный ход
   * работы, а не сбой: подписывать нужно новые данные, а прежний конверт всё равно стал
   * негодным. Отдельное состояние — чтобы в журнале это не выглядело ошибкой.
   */
  SUPERSEDED: 'Заменена',
  ERROR: 'Ошибка',
};

/**
 * Что с подписью НА СТОРОНЕ ЕСУТД — отдельно от того, поставлена ли она у нас.
 *
 * Это разные вопросы, и путать их нельзя. Наша отметка «подписал такой-то в такое-то время»
 * появляется в момент осмотра; стадия ЕСУТД может при этом не уйти вовсе — например, когда
 * подписывает не тот работник, которого они ждут. Раньше интерфейс показывал одно
 * «подписано» и умалчивал о втором: диспетчер видел галочку, а у ЕСУТД подписи не было.
 *
 * `INTEGRATION_STATE_LABELS` для этого не годится: там ответ на вопрос «что с отправкой»,
 * а здесь — «принята ли подпись».
 */
export const SIGNATURE_DELIVERY_STATES = [
  'NONE',
  'SENDING',
  'AWAITING_WORKER',
  'ACCEPTED',
  'REJECTED',
  'NOT_SENT',
  'UNKNOWN',
] as const;
export type SignatureDeliveryState = (typeof SIGNATURE_DELIVERY_STATES)[number];

/** Читаются после приставки «ЕСУТД: », поэтому само слово в них не повторяется. */
export const SIGNATURE_DELIVERY_LABELS: Record<SignatureDeliveryState, string> = {
  NONE: 'ожидается',
  SENDING: 'подпись отправляется',
  AWAITING_WORKER: 'ждёт подписи работника',
  ACCEPTED: 'подпись принята',
  REJECTED: 'подпись не принята',
  NOT_SENT: 'подпись не ушла',
  UNKNOWN: 'нет данных',
};

/**
 * Пять условий готовности смены к выпуску (ТЗ п.3.2).
 * Порядок фиксирован — интерфейс показывает чек-лист в этом же порядке.
 */
export const READINESS_CONDITIONS = [
  'driverAssignmentConfirmed',
  'medicalPreTripPassed',
  'technicalPreTripPassed',
  'esutdWaybillIdReceived',
  'allFourSignatures',
] as const;
export type ReadinessCondition = (typeof READINESS_CONDITIONS)[number];

export const READINESS_LABELS: Record<ReadinessCondition, string> = {
  driverAssignmentConfirmed: 'Водитель подтвердил назначение',
  medicalPreTripPassed: 'Предрейсовый медосмотр пройден',
  technicalPreTripPassed: 'Предрейсовый техосмотр пройден',
  esutdWaybillIdReceived: 'Получен id ЭПЛ от ЕСУТД',
  allFourSignatures: 'Подписаны 4 обязательные подписи',
};

export type Readiness = Record<ReadinessCondition, boolean> & { ready: boolean };
